import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  PipelineCard, 
  PipelineStageData, 
  PipelineMetrics, 
  PipelineFilters,
  PipelineStage,
  PIPELINE_STAGE_CONFIG
} from '../types/pipeline';
import { pipelineService } from '../services/pipelineService';
import { toast } from 'react-hot-toast';

export const usePipeline = () => {
  const { user } = useAuth();
  const [pipelineData, setPipelineData] = useState<PipelineStageData[]>([]);
  const [metrics, setMetrics] = useState<PipelineMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Pipeline adatok betöltése
   */
  const loadPipelineData = useCallback(async (filters?: PipelineFilters) => {
    if (!user?.organizationId) {
      setError('Nincs szervezet hozzárendelve');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Filters kiegészítése organizationId-val
      const fullFilters: PipelineFilters = {
        ...filters,
        organizationId: user.organizationId
      };

      // Pipeline adatok lekérése
      const data = await pipelineService.getPipelineData(user.organizationId, fullFilters);
      setPipelineData(data);

      // Metrikák számítása
      const metricsData = await pipelineService.calculateMetrics(user.organizationId, fullFilters);
      setMetrics(metricsData);

    } catch (err) {
      console.error('Error loading pipeline data:', err);
      setError('Hiba a pipeline adatok betöltése során');
      toast.error('Hiba a pipeline adatok betöltése során');
    } finally {
      setLoading(false);
    }
  }, [user?.organizationId]);

  /**
   * Kártya mozgatása egyik stage-ből a másikba
   */
  const moveCard = useCallback(async (
    cardId: string, 
    fromStage: PipelineStage, 
    toStage: PipelineStage
  ) => {
    if (!user?.organizationId) {
      throw new Error('Nincs szervezet hozzárendelve');
    }

    try {
      // Optimistic update
      setPipelineData(prevData => {
        const newData = [...prevData];
        const fromStageData = newData.find(s => s.stage === fromStage);
        const toStageData = newData.find(s => s.stage === toStage);

        if (fromStageData && toStageData) {
          const cardIndex = fromStageData.cards.findIndex(c => c.id === cardId);
          if (cardIndex !== -1) {
            const [card] = fromStageData.cards.splice(cardIndex, 1);
            card.stage = toStage;
            toStageData.cards.push(card);

            // Értékek frissítése
            fromStageData.totalValue -= card.totalAmount;
            fromStageData.count -= 1;
            toStageData.totalValue += card.totalAmount;
            toStageData.count += 1;
          }
        }

        return newData;
      });

      // Backend frissítés
      await pipelineService.moveCardToStage(cardId, fromStage, toStage);

    } catch (err) {
      console.error('Error moving card:', err);
      // Rollback optimistic update
      loadPipelineData();
      throw err;
    }
  }, [user?.organizationId, loadPipelineData]);

  /**
   * Dokumentum konverzió (Q→O, O→D, D→I)
   */
  const convertDocument = useCallback(async (
    cardId: string,
    fromType: string,
    toType: string
  ) => {
    if (!user?.organizationId) {
      throw new Error('Nincs szervezet hozzárendelve');
    }

    try {
      await pipelineService.convertDocument(cardId, fromType, toType);
      
      // Pipeline újratöltése
      await loadPipelineData();
      
      toast.success(`Dokumentum konvertálva: ${fromType} → ${toType}`);
    } catch (err) {
      console.error('Error converting document:', err);
      toast.error('Hiba a dokumentum konverzió során');
      throw err;
    }
  }, [user?.organizationId, loadPipelineData]);

  /**
   * Metrikák frissítése
   */
  const refreshMetrics = useCallback(async (filters?: PipelineFilters) => {
    if (!user?.organizationId) return;

    try {
      const fullFilters: PipelineFilters = {
        ...filters,
        organizationId: user.organizationId
      };

      const metricsData = await pipelineService.calculateMetrics(user.organizationId, fullFilters);
      setMetrics(metricsData);
    } catch (err) {
      console.error('Error refreshing metrics:', err);
    }
  }, [user?.organizationId]);

  /**
   * Kártya keresése ID alapján
   */
  const findCard = useCallback((cardId: string): PipelineCard | null => {
    for (const stage of pipelineData) {
      const card = stage.cards.find(c => c.id === cardId);
      if (card) return card;
    }
    return null;
  }, [pipelineData]);

  /**
   * Stage adatok lekérése
   */
  const getStageData = useCallback((stage: PipelineStage): PipelineStageData | null => {
    return pipelineData.find(s => s.stage === stage) || null;
  }, [pipelineData]);

  return {
    pipelineData,
    metrics,
    loading,
    error,
    loadPipelineData,
    moveCard,
    convertDocument,
    refreshMetrics,
    findCard,
    getStageData
  };
};
